/**
 * A saved finding, as a file the reader owns.
 *
 * This is the v1 bridge to the personal corpus: no account, no server, no
 * upload — the browser writes a Markdown file with YAML frontmatter and hands
 * it over. The format is deliberately the one the bible-study-framework reads,
 * so a finding saved here drops straight into a study repository.
 *
 * It also defines the shape accounts will store later, and it stays forever as
 * the fallback for anyone who has no account or declines to have their study
 * content held: religious conviction is GDPR Article 9 data, and a reader who
 * would rather keep it on their own disk must never be worse off for it.
 */

import type { AnswerSummary, ClaimStatus } from './api'

/**
 * Epistemic status as the study framework names it.
 *
 * The engine's vocabulary and the framework's are not the same words for the
 * same three ideas, and translating at the boundary is what keeps either free
 * to change without silently rewriting the other's meaning.
 */
const CLAIM_TYPES: Record<ClaimStatus, string> = {
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  'text-asserted': 'asserted',
  inferred: 'synthesized',
  open: 'open',
}

const TITLE_LIMIT = 80

export function findingMarkdown(
  question: string,
  prose: string,
  summary: AnswerSummary,
  savedOn: Date,
): string {
  const lines: string[] = [
    '---',
    `title: ${quote(title(question))}`,
    `date: ${savedOn.toISOString().slice(0, 10)}`,
    `corpus: ${quote(summary.corpus)}`,
    `question: ${quote(question)}`,
    `versions: ${inlineList(summary.consulted_versions)}`,
    `references: ${inlineList(summary.cited_references)}`,
    `crossed_a_border: ${summary.crossed_a_border}`,
    'claims:',
  ]

  for (const claim of summary.claims) {
    lines.push(`  - claim: ${quote(claim.claim)}`)
    lines.push(`    claim_type: ${CLAIM_TYPES[claim.status]}`)
    lines.push(`    basis: ${quote(claim.basis)}`)
    if (claim.tier !== null) lines.push(`    tier: ${claim.tier}`)
    lines.push(`    versions: ${inlineList(claim.versions)}`)
    lines.push(`    references: ${inlineList(claim.references)}`)
  }
  if (summary.claims.length === 0) lines[lines.length - 1] = 'claims: []'

  lines.push('---', '', prose.trim(), '')
  return lines.join('\n')
}

export function findingFilename(question: string, savedOn: Date): string {
  const slug =
    title(question)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'finding'
  return `${savedOn.toISOString().slice(0, 10)}-${slug}.md`
}

/** Hand the file to the reader. Nothing leaves the browser. */
export function downloadFinding(
  question: string,
  prose: string,
  summary: AnswerSummary,
  savedOn: Date = new Date(),
): void {
  const blob = new Blob([findingMarkdown(question, prose, summary, savedOn)], {
    type: 'text/markdown;charset=utf-8',
  })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = findingFilename(question, savedOn)
  anchor.click()
  URL.revokeObjectURL(url)
}

function title(question: string): string {
  const cleaned = question.trim().replace(/\s+/g, ' ')
  if (cleaned.length <= TITLE_LIMIT) return cleaned
  return `${cleaned.slice(0, TITLE_LIMIT).trimEnd()}…`
}

/**
 * A double-quoted YAML scalar.
 *
 * Always quoted rather than quoted-when-necessary: scripture references contain
 * colons, questions contain quotation marks, and a bare scalar that happens to
 * start with one would produce a file that parses as something else entirely.
 */
function quote(value: string): string {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
}

function inlineList(values: string[]): string {
  return `[${values.map(quote).join(', ')}]`
}
